import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { ConnectedBankDto } from "@/types/tpaygate";
import { Building2, Link2Off } from "lucide-react";
import { formatDateTime } from "@/lib/utils";

interface Props {
  bank: ConnectedBankDto;
  /** Omitted when the viewer may not disconnect - the button is then not rendered. */
  onDisconnect?: (bank: ConnectedBankDto) => void;
}

export function BankConnectionCard({ bank, onDisconnect }: Props) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        {bank.bankLogoUrl ? (
          <img
            src={bank.bankLogoUrl}
            alt={bank.bankCode}
            className="h-10 w-10 rounded object-contain"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded bg-muted">
            <Building2 className="h-5 w-5 text-muted-foreground" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">
              {bank.bankName ?? bank.bankCode}
            </span>
            <Badge variant={bank.isConnected ? "default" : "secondary"}>
              {bank.isConnected ? "Connected" : "Disconnected"}
            </Badge>
          </div>
          <div className="mt-1 text-sm text-muted-foreground truncate">
            {bank.accountName} — {bank.accountNo}
          </div>
          {bank.vaNumber && (
            <div className="mt-0.5 font-mono text-xs text-muted-foreground">
              VA: {bank.vaNumber}
            </div>
          )}
          <div className="mt-1 text-xs text-muted-foreground">
            Connected {formatDateTime(bank.createdAt)}
          </div>
        </div>

        {onDisconnect && (
          <Button
            variant="outline"
            size="sm"
            disabled={!bank.isConnected}
            onClick={() => onDisconnect(bank)}
          >
            <Link2Off className="mr-1 h-3 w-3" />
            Disconnect
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
